import React, { useState, useEffect } from 'react';
import { Crown } from 'lucide-react';
import { useProfile } from '@/context/ProfileContext';
import { useInventory } from '@/context/InventoryContext';
import { authFetch } from '@/utils/authFetch';
import { FUNCTION_URLS } from '@/config/functionUrls';
import { openStripeCheckout } from '@/utils/stripeCheckout';
import { getPurchaseProvider } from '@/utils/purchaseAvailability';
import { purchasePremium, restorePurchases, openManageSubscription } from '@/utils/playBilling';
import {
  Card,
  Heading,
  Body,
  MonoLabel,
  Button,
  showToast,
} from '@/components/rc';
import { CONFIG } from '@/constants';
import { trackEvent } from '@/config/analytics';

const PlanSection: React.FC = () => {
  const { profile, isPremium, hasSubscription } = useProfile();
  const { totalBottles } = useInventory();

  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [manageLoading, setManageLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  // null on a platform with no purchase path at all — see
  // utils/purchaseAvailability. The plan status itself still shows either way.
  const provider = getPurchaseProvider();
  const canPurchase = provider !== null;

  // Show success toast when returning from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') === 'success') {
      trackEvent('upgrade_completed');
      showToast({ tone: 'success', message: 'Welcome to Premium! Your upgrade is active.' });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleUpgrade = async () => {
    setUpgradeLoading(true);

    if (provider === 'play') {
      const outcome = await purchasePremium();
      if (outcome.status === 'success') {
        // Confirmed with Google immediately; the tier write lands a moment
        // later via the webhook, and the profile listener picks it up from
        // there — same "arrives shortly, not instantly" story as Stripe.
        showToast({ tone: 'success', message: 'Purchase complete — activating your Premium access...' });
      } else if (outcome.status === 'error') {
        showToast({ tone: 'error', message: outcome.message });
      }
      setUpgradeLoading(false);
      return;
    }

    try {
      const res = await authFetch(FUNCTION_URLS.createCheckout, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to start checkout');
      }
      const { url } = await res.json();
      if (url) await openStripeCheckout(url);
    } catch (e: any) {
      showToast({ tone: 'error', message: e.message || 'Something went wrong' });
      setUpgradeLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setManageLoading(true);

    if (provider === 'play') {
      // Opens Play's own subscription page — never throws, see playBilling.ts.
      await openManageSubscription();
      setManageLoading(false);
      return;
    }

    try {
      const res = await authFetch(FUNCTION_URLS.createPortal, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to open subscription portal');
      }
      const { url } = await res.json();
      if (url) await openStripeCheckout(url);
    } catch (e: any) {
      showToast({ tone: 'error', message: e.message || 'Something went wrong' });
      setManageLoading(false);
    }
  };

  const handleRestore = async () => {
    setRestoreLoading(true);
    const outcome = await restorePurchases();
    if (outcome.status === 'success') {
      showToast({ tone: 'success', message: 'Purchase restored — activating your Premium access...' });
    } else if (outcome.status === 'error') {
      showToast({ tone: 'error', message: outcome.message });
    }
    setRestoreLoading(false);
  };

  return (
    <>
      <Heading scale="subhead" className="mb-2">Plan</Heading>
      <Card elevation="flat">
        <div className="py-[var(--rc-row-padding-v)] px-[var(--rc-row-padding-h)]">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center"
              style={{
                backgroundColor: isPremium ? 'var(--rc-accent-pink)' : 'var(--rc-surface-tertiary)',
              }}
            >
              <Crown size={18} style={{ color: isPremium ? 'var(--rc-ink-on-accent)' : 'var(--rc-ink-tertiary)' }} />
            </div>
            <div>
              <Body className="w-auto font-semibold">
                {profile.lifetimeAccess
                  ? 'Premium (lifetime)'
                  : isPremium
                    ? profile.cancelAtPeriodEnd
                      ? `Premium (expires ${profile.cancelAt ? new Date(profile.cancelAt).toLocaleDateString() : 'soon'})`
                      : 'Premium'
                    : 'Free'}
              </Body>
              <MonoLabel size="label" colour="ghost" className="w-auto">
                {profile.lifetimeAccess
                  ? 'Unlimited bottles & full R\u00e9my access \u2014 no billing'
                  : isPremium
                    ? profile.cancelAtPeriodEnd
                      ? 'Your subscription will not renew'
                      : 'Unlimited bottles & full R\u00e9my access'
                    : `${totalBottles} of ${CONFIG.FREE_TIER.MAX_BOTTLES} bottles used`}
              </MonoLabel>
              {profile.subscriptionStatus === 'past_due' && !profile.lifetimeAccess && (
                <MonoLabel size="label" className="w-auto text-[var(--rc-accent-coral)]">
                  {canPurchase
                    ? 'Payment past due — please update your card'
                    : 'Payment past due'}
                </MonoLabel>
              )}
            </div>
          </div>
          {isPremium && hasSubscription && !profile.lifetimeAccess ? (
            canPurchase ? (
              <Button
                variantType="Secondary"
                label={manageLoading ? 'Opening...' : 'Manage Subscription'}
                onClick={handleManageSubscription}
                disabled={manageLoading}
                className="w-full"
              />
            ) : null /* No pointer to an external billing surface — naming one
                         is the steering the gate exists to avoid. */
          ) : !isPremium ? (
            <>
              {/* Usage bar */}
              <div className="mb-4">
                <div className="h-2 rounded-full bg-[var(--rc-surface-tertiary)] overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${Math.min((totalBottles / CONFIG.FREE_TIER.MAX_BOTTLES) * 100, 100)}%`,
                      backgroundColor: totalBottles >= CONFIG.FREE_TIER.MAX_BOTTLES
                        ? 'var(--rc-accent-coral)'
                        : 'var(--rc-accent-pink)',
                    }}
                  />
                </div>
              </div>
              {canPurchase && (
                <Button
                  variantType="Primary"
                  label={upgradeLoading ? (provider === 'play' ? 'Processing...' : 'Redirecting...') : 'Upgrade to Rave Cave Reserve'}
                  onClick={handleUpgrade}
                  disabled={upgradeLoading || restoreLoading}
                  className="w-full"
                />
              )}
              {provider === 'play' && (
                <button
                  onClick={handleRestore}
                  disabled={restoreLoading || upgradeLoading}
                  className="w-full mt-2 font-[var(--rc-font-mono)] text-[11px] uppercase tracking-wider text-[var(--rc-ink-ghost)] hover:text-[var(--rc-accent-pink)] transition-colors disabled:opacity-50"
                >
                  {restoreLoading ? 'Restoring...' : 'Restore Purchases'}
                </button>
              )}
            </>
          ) : null}
        </div>
      </Card>
    </>
  );
};

export default PlanSection;
